import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";
import * as Yup from "yup";

import { Button, Center, useToast } from "@chakra-ui/react";
import { SmartForm } from "@equidefi/portals/components/smart-form";
import { useSendResetPasswordEmail } from "@equidefi/portals/hooks/useUsers";
import { Heading, Text } from "@equidefi/ui";

import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import LoginContainer from "./LoginContainer";

const forgotSchema = Yup.object().shape({
  email: Yup.string().email("Please enter a valid email").required("Email is required."),
});

type TFormValues = {
  email: string;
};

const Forgot = () => {
  useDocumentTitle("Forgot Password");
  const { state } = useLocation();
  const redirectUrl = state?.redirectUrl || "/";

  const navigate = useNavigate();
  const toast = useToast();

  const resetPassword = useSendResetPasswordEmail();

  const form = useForm<TFormValues>({
    defaultValues: { email: "" },
    resolver: yupResolver(forgotSchema),
    mode: "onTouched",
  });

  const onSubmit = async ({ email }: TFormValues) => {
    try {
      await resetPassword.mutateAsync({
        email,
        source: "investor",
        redirect_to: redirectUrl,
      });

      toast({
        status: "info",
        description: `An email has been sent to ${email} with instructions to reset your password.`,
      });

      navigate(redirectUrl);
    } catch (error) {
      console.error(error);
      toast({
        status: "error",
        description: error?.response?.data?.errors?.join(". "),
      });
    } finally {
      form.reset();
    }
  };

  return (
    <LoginContainer>
      <SmartForm form={form} onSubmit={onSubmit} noValidate w="full">
        {({ Input }) => (
          <>
            <Heading color="white" fontWeight="bold" m="0" textStyle="h3">
              Forgot Password?
            </Heading>
            <Text color="white" mb="5" textStyle="body2">
              Enter your email to get a password reset link.
            </Text>
            <Input name="email" placeholder="Email Address" mb="5" h="12" />

            <Button
              isLoading={resetPassword.isLoading}
              isDisabled={!form.formState.isValid}
              type="submit"
              h="12"
              w="full">
              Continue
            </Button>

            <Center mt="5">
              <Text fontSize="sm" color="gray.400" mb="0" as={Link} _hover={{ color: "gray.500" }} to={redirectUrl}>
                Remember your password? Log in
              </Text>
            </Center>
          </>
        )}
      </SmartForm>
    </LoginContainer>
  );
};

export default Forgot;
